import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpIcon from '@mui/icons-material/Help';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Link as RouterLink } from 'react-router-dom';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { formatDate } from '@/ui/formatters/formatDate';
import { Box, ErrorOutline, Link as MuiLink, Text } from '@/ui/primitives';
import { overlayScrollbarSx } from '@/ui/primitives/overlayScrollbarSx';
import type { SummaryTooltipItem } from '../tableUtils';
import type { StatusChipStatus } from '@/ui/primitives';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactElement } from 'react';

type SkippedIconVariant = 'filled' | 'outlined';

const getTooltipStatusIcon = (
  status: StatusChipStatus | null,
  skippedIconVariant: SkippedIconVariant,
): SvgIconComponent => {
  if (status === 'untested' || status === null) return HelpIcon;
  if (status === 'passed' || status === 'completed') return CheckCircleOutlineIcon;
  if (status === 'running' || status === 'processing') return PlayCircleFilledIcon;
  if (status === 'queued' || status === 'waiting') return ScheduleIcon;
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'canceled' || status === 'timed_out') return CancelOutlinedIcon;
  if (status === 'warning') return ErrorOutline;
  if (status === 'errored') return ErrorOutline;
  return skippedIconVariant === 'outlined' ? BlockOutlinedIcon : RemoveCircleIcon;
};

const getTooltipStatusIconColor = (
  status: StatusChipStatus | null,
): ((theme: Parameters<typeof resolveSemanticColor>[0]) => string) => {
  if (status === 'passed' || status === 'completed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'success');
  }
  if (status === 'failed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'error');
  }
  if (status === 'running' || status === 'processing') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'info');
  }
  if (status === 'errored') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'brown');
  }
  if (status === 'warning') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'warning');
  }
  if (status === 'canceled' || status === 'timed_out') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'error');
  }
  if (status === 'queued' || status === 'waiting') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'textSecondary');
  }
  if (status === 'skipped') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'warning');
  }
  if (status === 'untested') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'textSecondary');
  }
  return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
    resolveSemanticColor(theme, 'textSecondary');
};

export const SummaryTooltipLines = ({ lines }: { lines: string[] }): ReactElement => {
  if (lines.length === 0) {
    return <>-</>;
  }
  return (
    <Stack gap="xxs">
      {lines.map((line, index) => (
        <Text key={`${line}-${String(index)}`} role="body" sx={{ color: 'inherit' }}>
          {line}
        </Text>
      ))}
    </Stack>
  );
};

const parseTooltipTimestamp = (value: Date | string | null): Date | null => {
  if (!value) return null;
  const timestamp = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(timestamp.valueOf())) return null;
  return timestamp;
};

const formatTooltipDateHeader = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);

const formatTooltipTimestamp = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value);

type SummaryTooltipContentProps = {
  items: SummaryTooltipItem[];
  linkMode: 'detail' | 'execution';
  skippedIconVariant?: SkippedIconVariant;
  showDetailLabel?: boolean;
  showTimestamp?: boolean;
  showDateHeaders?: boolean;
  disableLinks?: boolean;
};

const TOOLTIP_TIMESTAMP_COLUMN_WIDTH = '7ch';
const TOOLTIP_TIMESTAMP_TO_STATUS_GAP = 'sm';
const TOOLTIP_STATUS_TO_TEXT_GAP = 'sm';
const TOOLTIP_DETAIL_TO_RUN_GAP = 'sm';
const TOOLTIP_TEXT_SX = {
  fontSize: (theme: Parameters<typeof resolveSemanticColor>[0]) => theme.typography.pxToRem(15),
} as const;

export const SummaryTooltipContent = ({
  items,
  linkMode,
  skippedIconVariant = 'filled',
  showDetailLabel = true,
  showTimestamp = true,
  showDateHeaders = true,
  disableLinks = false,
}: SummaryTooltipContentProps): ReactElement => {
  if (items.length === 0) {
    return <Text role="body">-</Text>;
  }

  const shouldRenderTimestampColumn =
    showTimestamp && items.some((item) => parseTooltipTimestamp(item.timestamp) !== null);

  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: shouldRenderTimestampColumn
            ? `${TOOLTIP_TIMESTAMP_COLUMN_WIDTH} max-content`
            : 'max-content',
          columnGap: s(TOOLTIP_TIMESTAMP_TO_STATUS_GAP),
          rowGap: s('xxs'),
          alignItems: 'baseline',
          maxHeight: '240px',
          overflowY: 'auto',
          paddingY: s('xs'),
          paddingRight: s('md'),
          width: 'fit-content',
          minWidth: 'fit-content',
          maxWidth: 'max-content',
        },
        overlayScrollbarSx,
      ]}
    >
      {items.map((item, index) => {
        const href = linkMode === 'execution' ? (item.executionHref ?? item.href) : item.href;
        const detailLabel = item.detailLabel;
        const executionLabel = item.executionLabel;
        const fallbackSplitMatch =
          typeof detailLabel === 'string' ? /^(.*?)(?:\s*)(#\d+)$/.exec(detailLabel.trim()) : null;
        const resolvedDetailLabel =
          fallbackSplitMatch !== null ? fallbackSplitMatch[1].trim() : detailLabel;
        const resolvedExecutionLabel =
          executionLabel ?? (fallbackSplitMatch !== null ? fallbackSplitMatch[2].trim() : null);
        const StatusIcon = getTooltipStatusIcon(item.status, skippedIconVariant);
        const timestamp = parseTooltipTimestamp(item.timestamp);
        const previousTimestamp =
          index > 0 ? parseTooltipTimestamp(items[index - 1]?.timestamp ?? null) : null;
        const startsNewDateGroup =
          timestamp !== null &&
          (previousTimestamp === null ||
            timestamp.toISOString().slice(0, 10) !== previousTimestamp.toISOString().slice(0, 10));
        const timestampLabel = timestamp !== null ? formatTooltipTimestamp(timestamp) : '';
        const timestampTitle = formatDate(item.timestamp, { includeTime: true });
        const hasDetailLabel =
          showDetailLabel &&
          typeof resolvedDetailLabel === 'string' &&
          resolvedDetailLabel.trim().length > 0;
        const hasExecutionLabel =
          typeof resolvedExecutionLabel === 'string' && resolvedExecutionLabel.trim().length > 0;
        const canRenderSplitExecutionRow =
          !disableLinks &&
          hasDetailLabel &&
          hasExecutionLabel &&
          typeof item.executionHref === 'string' &&
          item.executionHref.length > 0;
        let singleLabel = '-';
        if (linkMode === 'execution') {
          if (hasExecutionLabel) {
            singleLabel = resolvedExecutionLabel;
          } else if (hasDetailLabel) {
            singleLabel = resolvedDetailLabel;
          }
        } else if (hasDetailLabel) {
          singleLabel = resolvedDetailLabel;
        } else if (hasExecutionLabel) {
          singleLabel = resolvedExecutionLabel;
        }
        const itemKey = `${item.key}-${String(index)}`;
        return (
          <Box key={itemKey} sx={{ display: 'contents' }}>
            {shouldRenderTimestampColumn && showDateHeaders && startsNewDateGroup ? (
              <Text
                role="body"
                sx={{
                  gridColumn: '1 / -1',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  fontSize: (theme) => theme.typography.pxToRem(15),
                  mt: index === 0 ? 0 : s('xs'),
                }}
              >
                {formatTooltipDateHeader(timestamp)}
              </Text>
            ) : null}
            {shouldRenderTimestampColumn ? (
              <Box
                title={timestampTitle}
                sx={{
                  display: 'block',
                  width: 'fit-content',
                  justifySelf: 'start',
                }}
              >
                <Text
                  role="body"
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    fontSize: (theme) => theme.typography.pxToRem(15),
                    flexShrink: 0,
                    display: 'block',
                    width: 'fit-content',
                    textAlign: 'left',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {timestampLabel}
                </Text>
              </Box>
            ) : null}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: s(TOOLTIP_STATUS_TO_TEXT_GAP),
                minWidth: 0,
                width: 'fit-content',
              }}
            >
              <Box
                component={StatusIcon}
                sx={{
                  fontSize: (theme) => theme.app.iconSizes.sm,
                  flexShrink: 0,
                  transform: 'translateY(2px)',
                  color: getTooltipStatusIconColor(item.status),
                }}
              />
              {canRenderSplitExecutionRow ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: s(TOOLTIP_DETAIL_TO_RUN_GAP),
                    width: 'fit-content',
                    minWidth: 0,
                  }}
                >
                  <MuiLink
                    component={RouterLink}
                    to={item.href}
                    underline="hover"
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      ...TOOLTIP_TEXT_SX,
                      display: 'inline-block',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {resolvedDetailLabel}
                  </MuiLink>
                  <MuiLink
                    component={RouterLink}
                    to={item.executionHref ?? item.href}
                    underline="hover"
                    color="text.secondary"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      ...TOOLTIP_TEXT_SX,
                      display: 'block',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {resolvedExecutionLabel}
                  </MuiLink>
                </Box>
              ) : disableLinks ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: s(TOOLTIP_DETAIL_TO_RUN_GAP),
                    width: 'fit-content',
                    minWidth: 0,
                  }}
                >
                  {hasDetailLabel ? (
                    <Text
                      role="body"
                      sx={{
                        ...TOOLTIP_TEXT_SX,
                        display: 'inline-block',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {resolvedDetailLabel}
                    </Text>
                  ) : null}
                  {hasExecutionLabel ? (
                    <Text
                      role="body"
                      sx={{
                        ...TOOLTIP_TEXT_SX,
                        color: hasDetailLabel ? 'text.secondary' : 'inherit',
                        display: 'block',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {resolvedExecutionLabel}
                    </Text>
                  ) : !hasDetailLabel ? (
                    <Text
                      role="body"
                      sx={{
                        ...TOOLTIP_TEXT_SX,
                        display: 'block',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {singleLabel}
                    </Text>
                  ) : null}
                </Box>
              ) : (
                <MuiLink
                  component={RouterLink}
                  to={href}
                  underline="hover"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  sx={{
                    ...TOOLTIP_TEXT_SX,
                    display: 'inline-block',
                    width: 'fit-content',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {singleLabel}
                </MuiLink>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
